"use client"

import { useEffect } from "react"

const TEXT_ENTRY = "input,textarea,select,[contenteditable]"

// Android grows the visual viewport past the layout one as its toolbar retracts, so this goes
// negative. iOS reveals a focused field by panning, and keeps reporting that pan as offsetTop after
// it has already shrunk the layout viewport by the same amount, so the pan counts only as far as
// the shrink. And it restores innerHeight ~300ms before visualViewport.height on dismissal, which
// reads as a keyboard still open — hence the focus check, since only a field can raise one.
const measure = (viewport: VisualViewport) => {
  if (viewport.scale !== 1) return 0
  const shrink = window.innerHeight - viewport.height
  if (shrink <= 0) return shrink
  if (!document.activeElement?.closest(TEXT_ENTRY)) return 0
  return shrink - Math.min(Math.max(viewport.offsetTop, 0), shrink)
}

export const useViewportInset = () => {
  useEffect(() => {
    const viewport = window.visualViewport
    if (!viewport) return
    const root = document.documentElement
    let frame = 0
    let lastInset = Number.NaN
    let lastHeight = Number.NaN

    const apply = () => {
      frame = 0
      const inset = Math.round(measure(viewport))
      const height = Math.round(viewport.height)
      if (inset !== lastInset) {
        lastInset = inset
        root.style.setProperty("--vv-inset", `${inset}px`)
      }
      if (height !== lastHeight) {
        lastHeight = height
        root.style.setProperty("--vv-height", `${height}px`)
      }
    }
    const schedule = () => {
      if (frame === 0) frame = requestAnimationFrame(apply)
    }

    apply()
    viewport.addEventListener("resize", schedule)
    viewport.addEventListener("scroll", schedule)
    window.addEventListener("scroll", schedule, { passive: true })
    window.addEventListener("orientationchange", schedule)
    window.addEventListener("focusin", schedule)
    window.addEventListener("focusout", schedule)
    return () => {
      if (frame !== 0) cancelAnimationFrame(frame)
      viewport.removeEventListener("resize", schedule)
      viewport.removeEventListener("scroll", schedule)
      window.removeEventListener("scroll", schedule)
      window.removeEventListener("orientationchange", schedule)
      window.removeEventListener("focusin", schedule)
      window.removeEventListener("focusout", schedule)
      root.style.removeProperty("--vv-inset")
      root.style.removeProperty("--vv-height")
    }
  }, [])
}
